import { useEffect, useState, useSyncExternalStore } from 'react';
import { Check, Languages } from '../../icons';
import { getMySettings, patchMySettings } from '../../api/users';
import { setSharedLang, isLang, type Lang } from '../../lib/base-i18n';
import {
  tSettings, getSettingsLang, subscribeSettingsLang, type SettingsI18nKey,
} from '../../lib/settings-i18n';

/**
 * 언어 설정 — 설정 모달 '환경설정' 전용 소형 필드.
 *
 * `EnvironmentSection`(통합설정 /settings)에는 언어가 테마·다크·알림 토글과 한 덩어리로 묶여
 * 있어 그대로 가져오면 환경설정 탭에 알림 토글까지 딸려온다. 그래서 **저장 경로는 같게**
 * (`patchMySettings({ language })` + 즉시 `setSharedLang`) 두고 화면만 언어 하나로 좁혔다.
 * 기존 파일은 건드리지 않는다 — 두 화면 중 어디서 바꾸든 같은 서버 값이 바뀐다.
 *
 * 드롭다운(Combobox) 대신 3-버튼: 모달 본문은 스크롤 컨테이너라 절대배치 팝업이 잘릴 수 있다
 * (Combobox 자체 주석의 알려진 한계). 선택지가 셋뿐이라 펼칠 이유도 없다.
 */

const LANGUAGES: ReadonlyArray<{ code: Lang; labelKey: SettingsI18nKey }> = [
  { code: 'ko', labelKey: 'settings.env.lang.ko' },
  { code: 'en', labelKey: 'settings.env.lang.en' },
  { code: 'ja', labelKey: 'settings.env.lang.ja' },
];

export function LanguageField() {
  useSyncExternalStore(subscribeSettingsLang, getSettingsLang, getSettingsLang);
  // 서버 조회 전에는 현재 화면 언어를 그대로 비춘다 — 로딩 문구 한 줄 때문에 화면이 덜컹이지 않게.
  const [value, setValue] = useState<Lang>(getSettingsLang());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getMySettings()
      .then((s) => { if (!cancelled && isLang(s.language)) setValue(s.language); })
      .catch(() => { /* best-effort — 실패해도 현재 언어로 표시 */ });
    return () => { cancelled = true; };
  }, []);

  function pick(next: Lang) {
    if (next === value) return;
    const prev = value;
    setValue(next);
    setError(null);
    // 화면은 즉시 전환하고(서버 왕복을 기다리지 않는다), 실패하면 되돌린다.
    setSharedLang(next);
    patchMySettings({ language: next }).catch((err) => {
      setValue(prev);
      setSharedLang(prev);
      setError(tSettings('settings.env.err_save', { msg: err?.message ?? '' }));
    });
  }

  return (
    <section className="settings-section" aria-labelledby="st-lang-h2">
      <h2 id="st-lang-h2">{tSettings('settings.modal.language.heading')}</h2>
      <p className="settings-section-subtitle">{tSettings('settings.modal.language.desc')}</p>

      <div className="st-optrow" role="radiogroup" aria-label={tSettings('settings.env.label_language')}>
        {LANGUAGES.map(({ code, labelKey }) => (
          <button
            key={code}
            type="button"
            role="radio"
            aria-checked={value === code}
            className={`st-opt${value === code ? ' is-on' : ''}`}
            onClick={() => pick(code)}
          >
            {value === code ? <Check size={14} aria-hidden /> : <Languages size={14} aria-hidden />}
            {tSettings(labelKey)}
          </button>
        ))}
      </div>

      {error && <p className="settings-error" role="alert">{error}</p>}
    </section>
  );
}
